import { DateRange, PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subYears,
} from 'date-fns';
import { PropsWithChildren, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeContext } from './DateRangeProvider.context';
import {
  calculateReferenceDateRange,
  DEFAULT_DATE_RANGE,
  getPreviousMonthDateRange,
  getPreviousQuarterDateRange,
  TODAY,
} from './DateRangeProvider.util';

interface DateRangeProviderProps {
  initialDateRangeDefault?: DateRange<Date>;
}

export const DateRangeProvider = ({ initialDateRangeDefault, children }: PropsWithChildren<DateRangeProviderProps>) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DateRangeProvider' });
  const [value, setValue] = useState<DateRange<Date>>(initialDateRangeDefault ?? DEFAULT_DATE_RANGE);

  const shortcutsItems: PickersShortcutsItem<DateRange<Date>>[] = useMemo(
    () => [
      {
        label: t('options.currentMonth'),
        getValue: () => {
          return [startOfMonth(TODAY), endOfMonth(TODAY)];
        },
      },
      {
        label: t('options.previousMonth'),
        getValue: () => getPreviousMonthDateRange(TODAY),
      },
      {
        label: t('options.currentQuarter'),
        getValue: () => {
          return [startOfQuarter(TODAY), endOfQuarter(TODAY)];
        },
      },
      {
        label: t('options.previousQuarter'),
        getValue: () => getPreviousQuarterDateRange(TODAY),
      },
      {
        label: t('options.last12Months'),
        getValue: () => {
          return [subMonths(TODAY, 12), TODAY];
        },
      },
      {
        label: t('options.last24Months'),
        getValue: () => {
          return [subMonths(TODAY, 24), TODAY];
        },
      },
      {
        label: t('options.currentYear'),
        getValue: () => {
          return [startOfYear(TODAY), endOfYear(TODAY)];
        },
      },
      {
        label: t('options.previousYear'),
        getValue: () => {
          const startOfPrevYear = subYears(startOfYear(TODAY), 1);
          const endOfPrevYear = subYears(endOfYear(TODAY), 1);
          return [startOfPrevYear, endOfPrevYear];
        },
      },
    ],
    [t],
  );
  return (
    <DateRangeContext.Provider
      value={{
        today: TODAY,
        currentDateRange: value,
        setCurrentDateRange: setValue,
        referenceDateRange: calculateReferenceDateRange(value),
        shortcuts: shortcutsItems,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};
